import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Subscription, SubscriptionId, SubscriptionStatus } from '../../api';
import { reasonMessage } from '../../i18n';
import { keepsKeyForRetry } from '../../lib/api/idempotency-key';

import { type PauseResumeCommand, type PauseResumeState, usePauseResume } from './use-pause-resume';

/** Props of {@link PauseResumeControl}. */
interface PauseResumeControlProps {
  readonly subscriptionId: SubscriptionId;
  /** The channel's current status, as E7 shows it. */
  readonly status: SubscriptionStatus;
  /** Called with the subscription the API returned after a pause or a resume. */
  readonly onChanged: (subscription: Subscription) => void;
}

/**
 * "Pause this channel" / "Resume this channel" on E7 (M3 R13; mockup 6, under Save).
 *
 * - Pause when the channel is active or suspended, Resume when it is paused; nothing for a
 *   channel never set up (there is nothing to pause, and no copying without settings, M3 R5).
 *   The button follows the one `status` the API returns. **Proposal, to be validated (Q105),
 *   M3 §5 `effectiveStatus`:** a trader's pause shows `PAUSED` whatever limit or external
 *   problem holds, so a paused channel always offers Resume; a resume shows the suspension that
 *   still holds. Q105's options: (a) a `pausedByTrader` flag drives this button, (b) a
 *   suspension shows above the pause, and a paused channel under a limit then offers Pause.
 * - No confirmation dialog: a pause reduces risk, and the button's text states the effect (R13,
 *   provisional; the resume control is on no mockup).
 * - A pause that cancelled pending validation requests says how many (R3, AC3).
 * - A failure shows only its C2 text (I12); after a network failure or a 5xx, Retry repeats the
 *   same action with the same key (AC9). A final refusal (409 `CHANNEL_NOT_CONFIGURED`) has no
 *   Retry (AC5).
 * - A failure belongs to the action the button offers when it failed: when the status changes
 *   (a refresh, another device), the failure and its key are dropped, and a failure of the other
 *   action is never shown, so Retry never repeats an action the trader no longer sees. The
 *   "N requests cancelled" notice goes too once a refresh shows another status than the pause
 *   returned (e.g. another device resumed the channel).
 */
export function PauseResumeControl({
  subscriptionId,
  status,
  onChanged,
}: PauseResumeControlProps): ReactElement | null {
  const { t } = useTranslation();
  const { state, run, reset } = usePauseResume(subscriptionId, onChanged);
  useEffect(() => {
    reset(status);
  }, [status, reset]);
  if (status === 'NOT_CONFIGURED') return null;
  const command: PauseResumeCommand = status === 'PAUSED' ? 'resume' : 'pause';
  const busy = state.status === 'busy';
  const label = busy
    ? t(
        state.command === 'pause'
          ? 'settings.pauseResume.pausing'
          : 'settings.pauseResume.resuming',
      )
    : t(command === 'pause' ? 'settings.pauseResume.pause' : 'settings.pauseResume.resume');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {state.status === 'done' &&
        state.resultStatus === status &&
        state.cancelledPendingValidations > 0 && (
          <Alert severity="info">
            {t('settings.pauseResume.cancelled', { count: state.cancelledPendingValidations })}
          </Alert>
        )}
      {state.status === 'failed' && state.command === command && (
        <Failure state={state} onRetry={run} />
      )}
      <Button
        variant="text"
        color="inherit"
        disabled={busy}
        aria-busy={busy}
        onClick={() => {
          void run(command);
        }}
        sx={{ minHeight: 44, fontWeight: 600, fontSize: 14, color: 'text.secondary' }}
      >
        {label}
      </Button>
    </Box>
  );
}

function Failure({
  state,
  onRetry,
}: {
  readonly state: Extract<PauseResumeState, { status: 'failed' }>;
  readonly onRetry: (command: PauseResumeCommand) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const message = reasonMessage(state.error.reasonCode, state.error.params);
  return (
    <Alert
      severity="error"
      action={
        keepsKeyForRetry(state.error) ? (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              void onRetry(state.command);
            }}
            sx={{ minHeight: 36 }}
          >
            {t('settings.retry')}
          </Button>
        ) : undefined
      }
    >
      {t(message.key, message.values)}
    </Alert>
  );
}
